import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import merubotIcon from '../../../assets/images/merubot_icon.png';
import { useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  // { label: 'Demo', id: 'demo-video' },
  { label: 'How it works', id: 'how' },
  { label: 'Features', id: 'features' },
  { label: 'Use cases', id: 'usecases' },
  // { label: 'Try Interview', id: 'ait-section' },
  { label: 'Pricing', id: 'pricing' },
];

// Negative = scroll further DOWN past the element's top edge (into the section content)
// Positive = stop earlier (leave space above)
// Tweak 'pricing' until the "Simple, Transparent Pricing" heading is visible right away
const SCROLL_OFFSETS = {
  pricing: -90,  // ← scrolls 120px past the #pricing div top, adjust as needed
  default: 0,
};

export default function Navbar({ onDemoClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [active, setActive] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const lastScrollY = useRef(0);
  const scrollUpDistance = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const observers = [];
    const sectionMap = {};

    NAV_LINKS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          sectionMap[id] = entry.isIntersecting;
          const current = NAV_LINKS.find(l => sectionMap[l.id]);
          setActive(current?.id || '');
        },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    const onScroll = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 40);

      if (currentY < 80) {
        setVisible(true);
        scrollUpDistance.current = 0;
      } else if (currentY < lastScrollY.current) {
        scrollUpDistance.current += lastScrollY.current - currentY;
        if (scrollUpDistance.current > 300) {
          setVisible(true);
          scrollUpDistance.current = 0;
        }
      } else if (currentY > lastScrollY.current + 6) {
        setVisible(false);
        setMenuOpen(false);
        scrollUpDistance.current = 0;
      }

      lastScrollY.current = currentY;
    };

    const onMouseMove = (e) => {
      if (e.clientY < 80) setVisible(true);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    return () => {
      observers.forEach(obs => obs.disconnect());
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const extraOffset = SCROLL_OFFSETS[id] ?? SCROLL_OFFSETS.default;
    // getBoundingClientRect().top gives position relative to viewport top.
    // Adding scrollY converts to absolute page position.
    // Subtracting extraOffset nudges the landing point down into the section.
    const top = el.getBoundingClientRect().top + window.scrollY - extraOffset;
    window.scrollTo({ top, behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <>
      <nav className={[
        'navbar',
        scrolled ? 'navbar--scrolled' : '',
        !visible ? 'navbar--hidden' : '',
      ].filter(Boolean).join(' ')}>

        {/* ── Logo icon — clicks to hero ── */}
        <button className="nav-logo-btn" onClick={() => scrollTo('hero')} aria-label="Go to top">
          <img src={merubotIcon} alt="merubot" className="nav-logo-icon" />
        </button>

        <div className="nav-divider" />

        <ul className="nav-links">
          {NAV_LINKS.map(({ label, id }) => (
            <li key={id}>
              <button
                className={`nav-link-btn${active === id ? ' active' : ''}`}
                onClick={() => scrollTo(id)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>

        <div className="nav-divider" />

        <button className="nav-cta" onClick={() => scrollTo('demo-form')}>
          Request demo
        </button>
        <button className="nav-cta" onClick={() => navigate('/email/redirect')}>
          Sign In
        </button>

        {/* Hamburger — mobile only */}
        <button
          className={`hamburger${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>

      </nav>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="mobile-menu">
          <button className="mobile-link" onClick={() => scrollTo('hero')}>
            🏠 Home
          </button>
          {NAV_LINKS.map(({ label, id }) => (
            <button key={id} className="mobile-link" onClick={() => scrollTo(id)}>
              {label}
            </button>
          ))}
          <button
            className="mobile-cta"
            onClick={() => { scrollTo('demo-form'); setMenuOpen(false); }}
          >
            Request Demo
          </button>
          <button
            className="mobile-cta"
            onClick={() => { navigate('/login'); setMenuOpen(false); }}
          >
            Sign In
          </button>
        </div>
      )}
    </>
  );
}